import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useContactCenterTheme } from "../../virtual-receptionist/theming/ContactCenterThemeContext";
import { MessageGroupMention } from "../messageGroups.types";

interface MentionTextProps {
  message: any;
  sender?: { type?: string; uid?: string };
}

interface TextSegment {
  text: string;
  mention: MessageGroupMention | null;
}

const buildSegments = (text: string, mentions: MessageGroupMention[]): TextSegment[] => {
  const valid = mentions
    .filter((mention) => Number.isFinite(mention?.start) && Number.isFinite(mention?.length) && mention.start >= 0 && mention.length > 0)
    .sort((a, b) => a.start - b.start);

  const segments: TextSegment[] = [];
  let cursor = 0;

  valid.forEach((mention) => {
    if (mention.start < cursor) return;
    if (mention.start > cursor) segments.push({ text: text.slice(cursor, mention.start), mention: null });
    segments.push({ text: text.slice(mention.start, mention.start + mention.length), mention });
    cursor = mention.start + mention.length;
  });

  if (cursor < text.length) segments.push({ text: text.slice(cursor), mention: null });

  return segments;
};

export const MentionText = ({ message, sender }: MentionTextProps) => {
  const { theme } = useContactCenterTheme();
  const text = message?.content?.text ?? "";
  const mentions: MessageGroupMention[] = Array.isArray(message?.mentions) ? message.mentions : [];

  const segments = useMemo(() => (mentions.length ? buildSegments(text, mentions) : [{ text, mention: null }]), [text, mentions]);

  return (
    <Typography
      variant="body2"
      sx={{
        color: "inherit",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontSize: "0.95rem",
        lineHeight: 1.6,
        letterSpacing: 0.1,
        fontWeight: 400,
      }}
    >
      {segments.map((segment, index) => {
        if (!segment.mention) return <span key={index}>{segment.text}</span>;

        const isMe = segment.mention.type === sender?.type && segment.mention.uid === sender?.uid;

        return (
          <Box
            key={index}
            component="span"
            sx={{
              px: 0.5,
              borderRadius: "6px",
              fontWeight: 600,
              color: isMe ? "#fff" : theme.shell.accent,
              backgroundColor: isMe ? theme.shell.accent : "transparent",
            }}
          >
            {segment.text}
          </Box>
        );
      })}
    </Typography>
  );
};
